import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";
import { Body } from "../../../components/main-body/body";
import ApiAdminBackfill from "../../../api/admin-backfill";

// LF360-1842. A clone used to copy a project's enrollment fields but neither its
// scenario nor the fields' node links, so in the cloned project the invitation form
// fell back to the default questions and the profile could not render the cohort —
// an empty select and a Save button that never enabled.
//
// New clones are fixed in code. Projects cloned before that keep the broken data
// until it is repaired, which is what this screen does — the same repair the
// command line runs, so the two cannot behave differently.
const ClonedEnrollmentRepair = () => {
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(null);
  const [report, setReport] = useState(null);
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ApiAdminBackfill.getClonedEnrollmentReport();
      setReport(data);
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message || "Could not load the report"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const repair = async (row, force) => {
    setRepairing(row.projectId);
    try {
      const { data } = await ApiAdminBackfill.repairClonedEnrollment({
        projectIds: [row.projectId],
        force,
      });
      const result = (data?.results || []).find((r) => r.projectId === row.projectId);
      setResults((prev) => ({ ...prev, [row.projectId]: result }));

      if (result?.status === "repaired") {
        message.success(`${row.code}: ${result.attached} field(s) attached`);
      } else if (result?.status === "skipped") {
        message.warning(`${row.code}: ${result.reason}`);
      } else {
        message.error(`${row.code}: ${result?.reason || "failed"}`);
      }
      await load();
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message || "The repair could not be started"
      );
    } finally {
      setRepairing(null);
    }
  };

  const columns = [
    {
      title: "Project",
      dataIndex: "title",
      render: (title, row) => (
        <span>
          {title} <Tag>{row.code}</Tag>
        </span>
      ),
    },
    {
      title: "Cloned from",
      dataIndex: "source",
      render: (source, row) =>
        source ? (
          <Tooltip title={`identified via ${row.via}`}>
            {source.title} <Tag>{source.code}</Tag>
          </Tooltip>
        ) : (
          <Tag color="error">source is gone</Tag>
        ),
    },
    {
      title: "Detached fields",
      dataIndex: "detachedCount",
      render: (detached, row) => (
        <span>
          {detached} of {row.fieldCount}
          {row.draftDetachedCount ? `, ${row.draftDetachedCount} in the draft` : ""}
        </span>
      ),
    },
    {
      title: "Graph",
      dataIndex: "activeNodes",
      render: (activeNodes, row) => (
        <span>
          {activeNodes} active / {row.draftNodes} draft → {row.sourceNodes} from source
        </span>
      ),
    },
    { title: "Subjects", dataIndex: "subjects" },
    {
      title: "",
      key: "action",
      render: (unused, row) => {
        const result = results[row.projectId];

        // Without a source there is nothing to repair from; the call would only come
        // back skipped, so it is not offered.
        if (!row.source) {
          return (
            <Tooltip title="The project this was cloned from no longer exists, so there is no flow to copy back">
              <Button size="small" disabled>
                Cannot repair
              </Button>
            </Tooltip>
          );
        }

        const button = (
          <Button
            type="primary"
            size="small"
            loading={repairing === row.projectId}
            danger={row.needsForce}
            onClick={row.needsForce ? undefined : () => repair(row, false)}
          >
            {row.needsForce ? "Replace and repair" : "Repair"}
          </Button>
        );

        return (
          <Space orientation="vertical" size={4}>
            {row.needsForce ? (
              // Replacing someone's work is confirmed by the person doing it, not by
              // the screen deciding on their behalf.
              <Popconfirm
                title="Replace this project's own enrollment work?"
                description={
                  <div style={{ maxWidth: 360 }}>
                    <div>These will be replaced by the source's flow:</div>
                    <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
                      {row.pendingDraft && <li>an unsaved draft in the editor</li>}
                      {row.ownQuestions.map((q) => (
                        <li key={`own-${q}`}>{q}</li>
                      ))}
                      {(row.editedQuestions || []).map((q) => (
                        <li key={`edited-${q}`}>{q}</li>
                      ))}
                      {row.graphChanged && <li>the enrollment graph differs from the source</li>}
                    </ul>
                  </div>
                }
                okText="Replace them"
                okButtonProps={{ danger: true }}
                cancelText="Leave it alone"
                onConfirm={() => repair(row, true)}
              >
                {button}
              </Popconfirm>
            ) : (
              button
            )}
            {result?.status === "failed" && (
              <span style={{ color: "#cf1322" }}>{result.reason}</span>
            )}
          </Space>
        );
      },
    },
  ];

  const candidates = report?.candidates || [];
  const unresolved = report?.unresolved || [];
  const withoutScenario = report?.withoutScenario || [];
  const manualReview = report?.manualReview || [];
  const risky = candidates.filter((c) => c.needsForce);

  return (
    <Body header={{ title: "Cloned enrollment repair" }}>
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          title="What this repairs"
          description={
            "A project cloned before the fix kept its own default enrollment graph while its " +
            "fields still described the source's — so the invitation form showed the wrong " +
            "questions and the profile could not render the cohort. Repairing copies the " +
            "source's flow in and reattaches the fields to it. Each project is repaired in one " +
            "transaction: if anything fails, that project is left exactly as it was."
          }
        />

        {risky.length > 0 && (
          <Alert
            type="warning"
            showIcon
            title="Some of these hold work of their own"
            description={
              <div>
                Repairing replaces the project's enrollment flow with the source's. These
                have questions or an unsaved draft that the source does not have, and that
                work would be lost:
                <ul style={{ marginBottom: 0 }}>
                  {risky.map((row) => (
                    <li key={row.projectId}>
                      <b>{row.code}</b>
                      {row.pendingDraft ? " — an unsaved draft" : ""}
                      {row.ownQuestions.length
                        ? ` — its own: ${row.ownQuestions.join(", ")}`
                        : ""}
                      {(row.editedQuestions || []).length
                        ? ` — reworded: ${row.editedQuestions.join("; ")}`
                        : ""}
                      {row.graphChanged ? " — graph differs from source" : ""}
                    </li>
                  ))}
                </ul>
              </div>
            }
          />
        )}

        <Space>
          <Button onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>

        <Table
          rowKey="projectId"
          loading={loading}
          columns={columns}
          dataSource={candidates}
          pagination={false}
          locale={{
            emptyText: "Nothing to repair — every cloned field points at a node its graph has.",
          }}
        />

        {unresolved.length > 0 && (
          <Alert
            type="warning"
            showIcon
            title="Cannot tell where these came from"
            description={
              <ul style={{ marginBottom: 0 }}>
                {unresolved.map((row) => (
                  <li key={row.projectId}>
                    <b>{row.code}</b> — {row.detachedCount} detached field(s); {row.via}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        {withoutScenario.length > 0 && (
          <Alert
            type="info"
            showIcon
            title="Cloned, but with no enrollment scenario at all"
            description={
              <div>
                These use the legacy invitation path, which works. Giving them the
                source's flow is a decision rather than a repair, so they are listed
                and not offered here:{" "}
                {withoutScenario.map((row) => row.code).join(", ")}
              </div>
            }
          />
        )}

        {manualReview.length > 0 && (
          <Alert
            type="warning"
            showIcon
            title="Possible old field adoption — manual review required"
            description={
              <ul style={{ marginBottom: 0 }}>
                {manualReview.map((row) => (
                  <li key={row.projectId}>
                    <b>{row.code}</b> — {row.fields.map((field) => field.label || field.id).join(", ")}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </Space>
    </Body>
  );
};

export default ClonedEnrollmentRepair;
